/**
 * Ce que la fiche d'un élève laisse à la console — chantier « manuel vivant »,
 * Φ-MV-5 (MV-TUT-02, 03, 05, 06, 12).
 *
 * RÈGLE QUI GOUVERNE CE FICHIER, la même que `tuteur-format.ts` : les états,
 * les paliers, les libellés d'une séquence (« 2 sur 6 faits ») et la phrase que
 * l'élève lira d'un niveau limité sont écrits par le SERVEUR et affichés tels
 * quels. On ne trouve ici que ce que le contrat laisse à l'écran :
 *
 *  - nommer une notion sans son code (« 1.2 · Multiplier des puissances ») ;
 *  - dire une date civile et l'écart qui en sépare ;
 *  - le RÉSUMÉ d'une section repliée — des dénombrements, jamais un taux ;
 *  - les brouillons des trois saisies (objectif, séquence, séance) : leurs
 *    valeurs de départ, leurs règles dites UNE PAR UNE, le corps envoyé.
 *
 * Invariant tenu ici : aucune fonction ne divise, ne moyenne ni ne produit de
 * pourcentage (`DECISIONS-ARCHITECTE.md`, point 17 — même pour le tuteur).
 * Fonctions pures : elles s'éprouvent sans rendu (`manuel-tuteur-format.test.ts`).
 */
import {
  DUREE_SEANCE_MAX,
  DUREE_SEANCE_MIN,
  DUREE_SEANCE_PAR_DEFAUT,
  DUREE_SEANCE_PAS,
  LIBELLES_GENRE_DE_GESTE,
  LIBELLES_NATURE_BLOC,
  MAX_CARACTERES_TITRE_EVALUATION,
  MAX_ETAPES_PARCOURS,
  NIVEAUX_EXERCICE_NOMMES,
  NIVEAUX_MAXIMAUX,
  PALIERS_NOTION,
  denombrer,
  estDateCivile,
  type BlocParcoursBody,
  type CreerParcoursBody,
  type DeposerSequenceBody,
  type ElementSequenceDto,
  type ElementSequenceBody,
  type ErreurRecurrenteTuteurDto,
  type EtatParcours,
  type EtatSeanceTutorat,
  type EvaluationClasseBody,
  type GenreDeGeste,
  type Matiere,
  type ModifierObjectifBody,
  type NatureBloc,
  type NatureEvaluationClasse,
  type NiveauMaximal,
  type NotionDeRessourceDto,
  type NotionVueTuteurDto,
  type PalierNotion,
  type ParcoursEleveDto,
  type PoserObjectifBody,
  type PositionAttenteDto,
  type PreparationEvaluationDto,
  type SeanceTutoratDto,
  type SequenceTuteurDto,
  type VueEleveTuteurResponse,
} from '@silex/shared';

import { FUSEAU_CONSOLE } from './tuteur-format';

/* ─────────────────────────── les notions, les paliers ─────────────────────────── */

/**
 * « 1.2 · Multiplier des puissances » — une notion du chapitre se dit par son
 * rang et son titre, un prérequis (sans rang) par son titre seul. Jamais son
 * code (MV-UX-04).
 */
export function nomDeNotion(notion: NotionDeRessourceDto, rangChapitre: number): string {
  return notion.rang === null ? notion.titre : `${rangChapitre}.${notion.rang} · ${notion.titre}`;
}

/** Le rang court, pour une case de frise : « 1.2 ». */
export function rangCourt(notion: NotionDeRessourceDto, rangChapitre: number): string {
  return notion.rang === null ? '' : `${rangChapitre}.${notion.rang}`;
}

/** Nombre de crans de l'échelle — elle situe le mot, elle ne mesure rien. */
export const NB_CRANS_NOTION = PALIERS_NOTION.length;

/** Le rang du palier sur ses quatre crans, de 1 (« Nouveau ») à 4 (« Acquis »). */
export function cransDuPalier(palier: PalierNotion): number {
  return PALIERS_NOTION.indexOf(palier) + 1;
}

/** « 1 · Facile » — un niveau d'exercice, nommé. */
export function nomDeNiveau(niveau: 1 | 2 | 3 | 4): string {
  return `${niveau} · ${NIVEAUX_EXERCICE_NOMMES[niveau].nom}`;
}

/* ─────────────────────────── les dates civiles ─────────────────────────── */

const FMT_CIVIL = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
});

const FMT_CIVIL_JOUR = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/**
 * « 12 octobre » — une DATE CIVILE (`AAAA-MM-JJ`), pas un instant. `formatDate`
 * la lirait à minuit UTC et l'afficherait la VEILLE dans le fuseau de la
 * console : une date d'objectif se formate donc en UTC, d'où elle ne bouge pas.
 */
export function jourCivil(date: string): string {
  return FMT_CIVIL.format(new Date(`${date.slice(0, 10)}T00:00:00Z`));
}

/** « lundi 12 octobre ». */
export function jourCivilLong(date: string): string {
  return FMT_CIVIL_JOUR.format(new Date(`${date.slice(0, 10)}T00:00:00Z`));
}

/** La date civile `jours` plus tard (ou plus tôt). */
export function ajouterJours(date: string, jours: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + jours);
  return d.toISOString().slice(0, 10);
}

/** Jours civils de `de` à `a`, bornes comprises — la largeur d'une case de frise. */
export function joursCompris(de: string, a: string): number {
  const ecart = Date.parse(`${a}T00:00:00Z`) - Date.parse(`${de}T00:00:00Z`);
  return Math.max(1, Math.round(ecart / 86_400_000) + 1);
}

/** « aujourd'hui », « demain », « dans 6 jours », « passée ». */
export function dansCombien(joursRestants: number): string {
  if (joursRestants < 0) return 'passée';
  if (joursRestants === 0) return 'aujourd’hui';
  if (joursRestants === 1) return 'demain';
  return `dans ${denombrer(joursRestants, 'jour')}`;
}

/* ─────────────────────────── les résumés des sections ─────────────────────────── */

/** Au-delà, la ligne de l'objectif ne tiendrait plus sur une ligne : le reste se compte. */
const EVALUATIONS_DANS_LE_RESUME = 2;

/**
 * « Du 5 au 30 octobre · Courte évaluation le 16 octobre · Évaluation le
 * 30 octobre · limité au niveau « Facile » » — L'OBJECTIF EN UNE LIGNE
 * (revue UX `UX-7-12`). Il change une fois par mois : replié, il ne pèse plus
 * sur les cinq onglets ; son détail reste derrière, et sa saisie en fenêtre.
 * Seules les évaluations À VENIR y figurent, la plus proche d'abord.
 */
export function resumeObjectif(
  vue: Pick<VueEleveTuteurResponse, 'objectif' | 'niveauMaximal'>,
): string {
  const { objectif, niveauMaximal } = vue;
  const niveau = niveauMaximal === null ? null : `limité au niveau « ${niveauMaximal.nomNiveau} »`;
  if (objectif === null) {
    return ['Aucun objectif défini', niveau].filter((m): m is string => m !== null).join(' · ');
  }
  const aVenir = objectif.evaluations.filter((e) => e.joursRestants >= 0);
  const reste = aVenir.length - EVALUATIONS_DANS_LE_RESUME;
  return [
    `Du ${jourCivil(objectif.debut)} au ${jourCivil(objectif.fin)}`,
    ...aVenir.slice(0, EVALUATIONS_DANS_LE_RESUME).map((e) => `${e.titre} le ${jourCivil(e.date)}`),
    reste > 0 ? denombrer(reste, 'autre évaluation', 'autres évaluations') : null,
    niveau,
  ]
    .filter((m): m is string => m !== null)
    .join(' · ');
}

/** « 14 exercices · 2 devoirs · 21 essais · 95 min de travail » — les comptes de l'élève, tels quels. */
export function direActivite(a: VueEleveTuteurResponse['activite']): string {
  return [
    denombrer(a.exercices, 'exercice'),
    denombrer(a.devoirs, 'devoir'),
    denombrer(a.essais, 'essai'),
    `${a.minutesDeTravail} min de travail`,
  ].join(' · ');
}

/** « 2 acquises · 1 à consolider · 3 pas encore travaillées » — un dénombrement par palier. */
export function resumeMaitrise(notions: readonly NotionVueTuteurDto[]): string {
  if (notions.length === 0) return 'Aucune notion dans ce chapitre.';
  const travaillees = notions.filter((n) => n.travaillee);
  if (travaillees.length === 0) return 'Aucun travail enregistré.';
  const compter = (palier: PalierNotion): number =>
    travaillees.filter((n) => n.palier === palier).length;
  const morceaux = [
    compter('acquis') > 0 ? denombrer(compter('acquis'), 'acquise') : null,
    compter('a_consolider') > 0 ? `${compter('a_consolider')} à consolider` : null,
    compter('en_progression') > 0 ? `${compter('en_progression')} en progression` : null,
    notions.length > travaillees.length
      ? `${notions.length - travaillees.length} pas encore ${
          notions.length - travaillees.length > 1 ? 'travaillées' : 'travaillée'
        }`
      : null,
  ].filter((m): m is string => m !== null);
  return morceaux.length > 0 ? morceaux.join(' · ') : denombrer(travaillees.length, 'notion');
}

/** La phrase d'une fiche où aucune erreur n'a encore été reconnue dans un travail. */
export const PHRASE_AUCUNE_ERREUR = 'Aucune erreur relevée pour l’instant.';

/**
 * « 2 erreurs relevées · aucune récurrente », « 3 erreurs relevées · 1
 * récurrente » (revue UX `UX-7-13`). Une erreur RELEVÉE a été reconnue dans au
 * moins un travail ; elle n'est RÉCURRENTE que si elle répond à la définition
 * du produit (`DECISIONS-ARCHITECTE.md`, point 5) — c'est le serveur qui le
 * dit, erreur par erreur (`recurrente`), et c'est ce que compte le filtre
 * « Erreur récurrente » de la liste. Les deux comptes sont dits côte à côte :
 * la section ne peut plus annoncer « 2 » quand le filtre compte 0.
 */
export function resumeErreurs(erreurs: readonly ErreurRecurrenteTuteurDto[]): string {
  if (erreurs.length === 0) return PHRASE_AUCUNE_ERREUR;
  const recurrentes = erreurs.filter((e) => e.recurrente).length;
  const tete = denombrer(erreurs.length, 'erreur relevée', 'erreurs relevées');
  return `${tete} · ${
    recurrentes === 0 ? 'aucune récurrente' : denombrer(recurrentes, 'récurrente')
  }`;
}

/** « Évaluation, dans 6 jours · En bonne voie » — la plus proche ; vide : la phrase de l'exigence. */
export function resumePreparations(preparations: readonly PreparationEvaluationDto[]): string {
  const premiere = preparations[0];
  if (premiere === undefined) return 'Aucune évaluation à venir dans l’objectif.';
  const tete = `${premiere.evaluation.titre}, ${dansCombien(premiere.evaluation.joursRestants)} · ${premiere.libelleEtat}`;
  return preparations.length === 1
    ? tete
    : `${tete} · ${denombrer(preparations.length - 1, 'autre à venir', 'autres à venir')}`;
}

/** « Aucune erreur ne le met en cause » ou « 2 blocages en amont ». */
export function resumePrerequis(
  prerequis: readonly NotionVueTuteurDto[],
  blocages: VueEleveTuteurResponse['blocagesEnAmont'],
): string {
  if (prerequis.length === 0) return 'Ce chapitre ne suppose aucun acquis d’avant.';
  const tete = denombrer(prerequis.length, 'prérequis', 'prérequis');
  return blocages.length === 0
    ? `${tete} · aucun blocage en amont`
    : `${tete} · ${denombrer(blocages.length, 'blocage en amont', 'blocages en amont')}`;
}

/** « 2 attentes atteintes · 3 pas encore » — un dénombrement d'états, pas un taux. */
export function resumePosition(attentes: readonly PositionAttenteDto[]): string {
  if (attentes.length === 0) return 'Aucune attente du programme n’est rattachée à ce chapitre.';
  const atteintes = attentes.filter((a) => a.etat === 'atteinte').length;
  const reste = attentes.length - atteintes;
  /* Seul, « 5 pas encore » ne dit pas de quoi l'on parle (vu sur la capture du 8 octobre). */
  if (atteintes === 0) {
    return `${denombrer(attentes.length, 'attente')}, aucune atteinte pour l’instant`;
  }
  return [
    atteintes > 0 ? denombrer(atteintes, 'attente atteinte', 'attentes atteintes') : null,
    reste > 0 ? `${reste} pas encore` : null,
  ]
    .filter((m): m is string => m !== null)
    .join(' · ');
}

/**
 * « Atteinte », « En route », ou « Pas commencée » quand AUCUN travail ne la
 * mesure (revue UX `UX-7-35` : la pastille disait « En route » sur une attente
 * à « 0 travail »).
 */
export function direEtatAttente(a: Pick<PositionAttenteDto, 'etat' | 'preuve'>): string {
  if (a.etat === 'atteinte') return 'Atteinte';
  return a.preuve.travaux === 0 ? 'Pas commencée' : 'En route';
}

/**
 * « Attente atteinte » ou « Pas encore atteinte — En progression ; 4 travaux,
 * niveau 2 réussi » : l'état et SA PREUVE, en comptes (MV-TUT-03).
 */
export function direPreuveAttente(a: PositionAttenteDto, libellePalier: string): string {
  const preuve = [
    denombrer(a.preuve.travaux, 'travail', 'travaux'),
    a.preuve.plusHautNiveauReussi === null
      ? 'aucun niveau réussi'
      : `niveau ${a.preuve.plusHautNiveauReussi} réussi`,
  ].join(', ');
  return a.etat === 'atteinte'
    ? `Attente atteinte — ${preuve}`
    : `Pas encore atteinte — ${libellePalier} ; ${preuve}`;
}

/* ─────────────────────────── l'objectif ─────────────────────────── */

/** Une évaluation en cours de saisie. `cle` ne part pas au serveur : c'est la clé de liste. */
export interface BrouillonEvaluation {
  readonly cle: string;
  readonly id?: string;
  readonly titre: string;
  readonly date: string;
  readonly nature: NatureEvaluationClasse;
  readonly notions: readonly string[];
}

export interface BrouillonObjectif {
  readonly chapitreCode: string;
  readonly debut: string;
  readonly fin: string;
  readonly evaluations: readonly BrouillonEvaluation[];
  readonly notionsCiblees: readonly string[];
  /** `null` : tous les niveaux ouverts — le défaut (F-14). */
  readonly niveauMaximal: NiveauMaximal | null;
}

/** Le lundi de la semaine de `date` (une date civile). */
export function lundiDeLaSemaine(date: string): string {
  const jour = new Date(`${date}T00:00:00Z`).getUTCDay(); // 0 = dimanche
  return ajouterJours(date, jour === 0 ? -6 : 1 - jour);
}

/** Durée proposée d'un chapitre quand l'élève n'a pas d'objectif : quatre semaines, du lundi au vendredi. */
const SEMAINES_PROPOSEES = 4;

/**
 * Le brouillon d'arrivée de l'éditeur : l'objectif actif s'il existe, sinon le
 * chapitre de la vue, du lundi de cette semaine au vendredi de la quatrième,
 * SANS évaluation (zéro évaluation est un objectif valide ; le tuteur ajoute
 * celles de la classe).
 */
export function brouillonDObjectif(vue: VueEleveTuteurResponse): BrouillonObjectif {
  const niveauMaximal = vue.niveauMaximal?.niveauMaximal ?? null;
  const { objectif } = vue;
  if (objectif === null) {
    const debut = lundiDeLaSemaine(vue.aujourdhui);
    return {
      chapitreCode: vue.chapitre.code,
      debut,
      fin: ajouterJours(debut, SEMAINES_PROPOSEES * 7 - 3),
      evaluations: [],
      notionsCiblees: [],
      niveauMaximal,
    };
  }
  return {
    chapitreCode: objectif.chapitre.code,
    debut: objectif.debut,
    fin: objectif.fin,
    evaluations: objectif.evaluations.map((e) => ({
      cle: e.id,
      id: e.id,
      titre: e.titre,
      date: e.date,
      nature: e.nature,
      notions: e.notions.map((n) => n.code),
    })),
    notionsCiblees: objectif.notionsCiblees.map((n) => n.code),
    niveauMaximal,
  };
}

/** « Ajouter une évaluation » : celle de fin de chapitre, à la date de fin, sur toutes les notions. */
export function evaluationDeFin(
  brouillon: BrouillonObjectif,
  codesNotions: readonly string[],
  cle: string,
): BrouillonEvaluation {
  return {
    cle,
    titre: 'Évaluation de fin de chapitre',
    date: brouillon.fin,
    nature: 'examen',
    notions: codesNotions,
  };
}

/**
 * Les règles de l'objectif, DITES UNE PAR UNE (MV-TUT-12 : la maquette n'avait
 * qu'un message pour toutes). Vide : le brouillon peut partir. Le serveur
 * revérifie tout, et sa raison — s'il refuse — s'affiche telle quelle.
 */
export function reglesDObjectif(b: BrouillonObjectif): string[] {
  const manques: string[] = [];
  if (!estDateCivile(b.debut)) manques.push('Indiquez le début du chapitre en classe.');
  if (!estDateCivile(b.fin)) manques.push('Indiquez la fin du chapitre en classe.');
  if (estDateCivile(b.debut) && estDateCivile(b.fin) && b.debut >= b.fin) {
    manques.push('La fin du chapitre doit venir après son début.');
  }
  b.evaluations.forEach((e, i) => {
    const laquelle = e.titre.trim() === '' ? `L’évaluation ${i + 1}` : `« ${e.titre.trim()} »`;
    if (e.titre.trim() === '') manques.push(`${laquelle} n’a pas d’intitulé.`);
    else if (e.titre.trim().length > MAX_CARACTERES_TITRE_EVALUATION) {
      manques.push(
        `${laquelle} : l’intitulé dépasse ${MAX_CARACTERES_TITRE_EVALUATION} caractères.`,
      );
    }
    if (!estDateCivile(e.date)) manques.push(`${laquelle} n’a pas de date.`);
    if (e.notions.length === 0) manques.push(`${laquelle} ne vise aucune notion.`);
  });
  return manques;
}

function corpsDesEvaluations(b: BrouillonObjectif): EvaluationClasseBody[] {
  return b.evaluations.map((e) => ({
    ...(e.id === undefined ? {} : { id: e.id }),
    titre: e.titre.trim(),
    date: e.date,
    nature: e.nature,
    notions: [...e.notions],
  }));
}

/** Le corps de `POST …/objectif` : un objectif neuf — les évaluations y sont créées, sans identifiant. */
export function corpsPoserObjectif(b: BrouillonObjectif): PoserObjectifBody {
  return {
    chapitreCode: b.chapitreCode,
    debut: b.debut,
    fin: b.fin,
    evaluations: corpsDesEvaluations(b).map(({ id: _id, ...reste }) => reste),
    notionsCiblees: [...b.notionsCiblees],
  };
}

/** Le corps de `PATCH /objectifs/:id` : la liste d'évaluations REMPLACE celle de l'objectif. */
export function corpsModifierObjectif(b: BrouillonObjectif): ModifierObjectifBody {
  return {
    debut: b.debut,
    fin: b.fin,
    evaluations: corpsDesEvaluations(b),
    notionsCiblees: [...b.notionsCiblees],
  };
}

/** Les positions du réglage « Niveaux » : tout ouvert d'abord (le défaut), puis « Limiter au niveau… ». */
export function choixDeNiveauMaximal(): readonly {
  readonly valeur: NiveauMaximal | null;
  readonly libelle: string;
}[] {
  return [
    { valeur: null, libelle: 'Tous les niveaux ouverts' },
    ...NIVEAUX_MAXIMAUX.map((n) => ({
      valeur: n,
      libelle: `Limiter au niveau ${nomDeNiveau(n)}`,
    })),
  ];
}

/* ─────────────────────────── la séquence ─────────────────────────── */

/** Un élément en cours de composition : ce qui part au serveur, et de quoi le lire à l'écran. */
export interface ElementCompose {
  readonly genre: GenreDeGeste;
  readonly code: string;
  /** Le titre de la ressource, ou l'énoncé d'un exercice sans titre. Texte riche. */
  readonly titre: string;
  /** « Exercice · 2 · Intermédiaire », « Fiche », « Capsule ». */
  readonly sorte: string;
  /** Pourquoi il est là ; vide : « Ajouté par le tuteur » (le serveur l'écrit). */
  readonly raison: string;
}

export const cleDElement = (e: Pick<ElementCompose, 'genre' | 'code'>): string =>
  `${e.genre}:${e.code}`;

/** Monter (`-1`) ou descendre (`+1`) l'élément de rang `index` ; hors bornes, la liste est rendue telle quelle. */
export function deplacer<T>(liste: readonly T[], index: number, pas: -1 | 1): T[] {
  const cible = index + pas;
  const copie = [...liste];
  const a = copie[index];
  const b = copie[cible];
  if (a === undefined || b === undefined) return copie;
  copie[index] = b;
  copie[cible] = a;
  return copie;
}

/** Les éléments d'une séquence déjà déposée, repris pour la modifier. */
export function elementsDeSequence(sequence: SequenceTuteurDto): ElementCompose[] {
  return sequence.elements.map((e) => ({
    genre: e.cible.genre,
    code: e.cible.code,
    titre: e.titre,
    sorte: e.nomNiveau === null ? e.libelleGenre : `${e.libelleGenre} · ${e.nomNiveau}`,
    raison: e.raison ?? '',
  }));
}

export interface BrouillonSequence {
  readonly titre: string;
  readonly mot: string;
  readonly pourLe: string;
  readonly elements: readonly ElementCompose[];
}

function corpsDesElements(elements: readonly ElementCompose[]): ElementSequenceBody[] {
  return elements.map((e) => ({
    genre: e.genre,
    code: e.code,
    ...(e.raison.trim() === '' ? {} : { raison: e.raison.trim() }),
  }));
}

/** Le corps de `POST …/sequences`. Un mot vide et une échéance vide ne partent pas. */
export function corpsDeposerSequence(b: BrouillonSequence): DeposerSequenceBody {
  return {
    titre: b.titre.trim(),
    elements: corpsDesElements(b.elements),
    ...(b.mot.trim() === '' ? {} : { mot: b.mot.trim() }),
    ...(b.pourLe === '' ? {} : { pourLe: b.pourLe }),
  };
}

/** Le corps de `PATCH /sequences/:id` : un mot ou une échéance vidés sont EFFACÉS (`null`). */
export function corpsModifierSequence(b: BrouillonSequence): {
  titre: string;
  mot: string | null;
  pourLe: string | null;
  elements: ElementSequenceBody[];
} {
  return {
    titre: b.titre.trim(),
    mot: b.mot.trim() === '' ? null : b.mot.trim(),
    pourLe: b.pourLe === '' ? null : b.pourLe,
    elements: corpsDesElements(b.elements),
  };
}

/* ─────────────────────────── le parcours (P3, AB#975) ─────────────────────────── */

/*
 * Le compositeur de séquence devient un compositeur de PARCOURS : des blocs
 * (une nature, un titre facultatif) qui portent les étapes, pris dans
 * plusieurs chapitres d'une même matière, 20 étapes au plus en tout
 * (`MAX_ETAPES_PARCOURS`, la borne de la liste du tuteur). Contrat :
 * `docs/pilotage/passe-ux-prod/CONTRAT-PARCOURS.md`.
 *
 * Le MÊME numéro partout (fondateur, 19 oct. 2026, AB#961) : une étape porte
 * son rang global, de 1 à 7, quel que soit son bloc ; le progrès se dit
 * « 3 sur 7 », jamais « Étape 4 sur 7 », jamais un taux.
 */

/** Un bloc en cours de composition. `cle` ne part pas au serveur : elle tient le rendu. */
export interface BlocCompose {
  readonly cle: string;
  readonly nature: NatureBloc;
  /** Vide : le titre de sa nature (`LIBELLES_NATURE_BLOC`), écrit par le serveur. */
  readonly titre: string;
  readonly etapes: readonly ElementCompose[];
}

export interface BrouillonParcours {
  readonly nom: string;
  readonly mot: string;
  readonly pourLe: string;
  readonly blocs: readonly BlocCompose[];
}

/** Un parcours neuf : un bloc vide, prêt à recevoir. */
export function parcoursVide(): BrouillonParcours {
  return {
    nom: '',
    mot: '',
    pourLe: '',
    blocs: [{ cle: 'b1', nature: 'libre', titre: '', etapes: [] }],
  };
}

/** Les étapes dans l'ordre global : leur place dans cette liste, plus un, est leur numéro. */
export function etapesDuParcours(b: BrouillonParcours): ElementCompose[] {
  return b.blocs.flatMap((bloc) => bloc.etapes);
}

export function nombreDEtapes(b: BrouillonParcours): number {
  return b.blocs.reduce((n, bloc) => n + bloc.etapes.length, 0);
}

/** Le numéro global de la première étape de chaque bloc, dans l'ordre des blocs. */
export function premiersNumeros(b: BrouillonParcours): number[] {
  const premiers: number[] = [];
  let suivant = 1;
  for (const bloc of b.blocs) {
    premiers.push(suivant);
    suivant += bloc.etapes.length;
  }
  return premiers;
}

/** « b3 » — une clé qu'aucun bloc du brouillon ne porte encore. */
function cleNeuve(b: BrouillonParcours): string {
  const pris = b.blocs.map((bloc) => Number(bloc.cle.slice(1))).filter(Number.isFinite);
  return `b${Math.max(0, ...pris) + 1}`;
}

export function ajouterBloc(b: BrouillonParcours, nature: NatureBloc = 'libre'): BrouillonParcours {
  return { ...b, blocs: [...b.blocs, { cle: cleNeuve(b), nature, titre: '', etapes: [] }] };
}

/** Retirer un bloc retire ses étapes ; le dernier bloc reste, vidé. */
export function retirerBloc(b: BrouillonParcours, cle: string): BrouillonParcours {
  if (b.blocs.length <= 1) {
    return { ...b, blocs: b.blocs.map((bloc) => ({ ...bloc, etapes: [] })) };
  }
  return { ...b, blocs: b.blocs.filter((bloc) => bloc.cle !== cle) };
}

export function changerBloc(
  b: BrouillonParcours,
  cle: string,
  morceau: Partial<Pick<BlocCompose, 'nature' | 'titre'>>,
): BrouillonParcours {
  return {
    ...b,
    blocs: b.blocs.map((bloc) => (bloc.cle === cle ? { ...bloc, ...morceau } : bloc)),
  };
}

/**
 * Ajouter une étape au bloc `cle`. Rien ne change si le parcours est plein ou
 * si la ressource y est déjà, dans n'importe quel bloc : le serveur refuserait
 * le doublon (`verifierBlocs`).
 */
export function ajouterEtape(
  b: BrouillonParcours,
  cle: string,
  e: ElementCompose,
): BrouillonParcours {
  if (nombreDEtapes(b) >= MAX_ETAPES_PARCOURS) return b;
  if (etapesDuParcours(b).some((x) => cleDElement(x) === cleDElement(e))) return b;
  return {
    ...b,
    blocs: b.blocs.map((bloc) =>
      bloc.cle === cle ? { ...bloc, etapes: [...bloc.etapes, e] } : bloc,
    ),
  };
}

export function retirerEtape(
  b: BrouillonParcours,
  e: Pick<ElementCompose, 'genre' | 'code'>,
): BrouillonParcours {
  const cle = cleDElement(e);
  return {
    ...b,
    blocs: b.blocs.map((bloc) => ({
      ...bloc,
      etapes: bloc.etapes.filter((x) => cleDElement(x) !== cle),
    })),
  };
}

export function changerRaisonDEtape(
  b: BrouillonParcours,
  e: Pick<ElementCompose, 'genre' | 'code'>,
  raison: string,
): BrouillonParcours {
  const cle = cleDElement(e);
  return {
    ...b,
    blocs: b.blocs.map((bloc) => ({
      ...bloc,
      etapes: bloc.etapes.map((x) => (cleDElement(x) === cle ? { ...x, raison } : x)),
    })),
  };
}

/**
 * Monter (`-1`) ou descendre (`+1`) une étape DANS L'ORDRE GLOBAL : en tête de
 * son bloc, monter la fait passer à la fin du bloc précédent ; en fin de bloc,
 * descendre la fait passer en tête du suivant. Hors bornes : inchangé.
 */
export function deplacerEtape(
  b: BrouillonParcours,
  e: Pick<ElementCompose, 'genre' | 'code'>,
  pas: -1 | 1,
): BrouillonParcours {
  const cle = cleDElement(e);
  const iBloc = b.blocs.findIndex((bloc) => bloc.etapes.some((x) => cleDElement(x) === cle));
  const bloc = b.blocs[iBloc];
  if (bloc === undefined) return b;
  const i = bloc.etapes.findIndex((x) => cleDElement(x) === cle);
  const etape = bloc.etapes[i];
  if (etape === undefined) return b;
  const cible = i + pas;
  if (cible >= 0 && cible < bloc.etapes.length) {
    const blocs = [...b.blocs];
    blocs[iBloc] = { ...bloc, etapes: deplacer(bloc.etapes, i, pas) };
    return { ...b, blocs };
  }
  const voisin = b.blocs[iBloc + pas];
  if (voisin === undefined) return b;
  const blocs = [...b.blocs];
  blocs[iBloc] = { ...bloc, etapes: bloc.etapes.filter((_, j) => j !== i) };
  blocs[iBloc + pas] = {
    ...voisin,
    etapes: pas === -1 ? [...voisin.etapes, etape] : [etape, ...voisin.etapes],
  };
  return { ...b, blocs };
}

/** Ce qui manque pour déposer, une règle par phrase. Vide : le parcours part. */
export function reglesDuParcours(b: BrouillonParcours): string[] {
  const total = nombreDEtapes(b);
  return [
    b.nom.trim() === '' ? 'Donnez un nom au parcours.' : null,
    total === 0 ? 'Ajoutez au moins une étape.' : null,
    total > MAX_ETAPES_PARCOURS
      ? `Un parcours compte ${MAX_ETAPES_PARCOURS} étapes au plus.`
      : null,
  ].filter((m): m is string => m !== null);
}

/** Le corps d'un dépôt du tuteur : `CreerParcoursBody` et son mot. */
export type CorpsDeposerParcours = CreerParcoursBody & { readonly mot?: string };

/**
 * Le corps de `POST …/parcours` (à un élève ou à une classe). Un bloc vide ne
 * part pas (le serveur n'en accepte pas) ; un titre, un mot, une date vides
 * non plus ; une raison vide laisse le serveur écrire « Ajouté par le tuteur ».
 */
export function corpsDeposerParcours(b: BrouillonParcours, matiere: Matiere): CorpsDeposerParcours {
  const blocs: BlocParcoursBody[] = b.blocs
    .filter((bloc) => bloc.etapes.length > 0)
    .map((bloc) => ({
      nature: bloc.nature,
      ...(bloc.titre.trim() === '' ? {} : { titre: bloc.titre.trim() }),
      etapes: corpsDesElements(bloc.etapes),
    }));
  return {
    nom: b.nom.trim(),
    matiere,
    blocs,
    ...(b.pourLe === '' ? {} : { pourLe: b.pourLe }),
    ...(b.mot.trim() === '' ? {} : { mot: b.mot.trim() }),
  };
}

/** Le titre qu'un bloc affiche : le sien, ou celui de sa nature. */
export const titreDeBloc = (bloc: Pick<BlocCompose, 'nature' | 'titre'>): string =>
  bloc.titre.trim() === '' ? LIBELLES_NATURE_BLOC[bloc.nature] : bloc.titre.trim();

/**
 * Le brouillon que la préparation de séance transmet (une liste à plat) devient
 * un parcours d'un seul bloc : rien n'est perdu, le tuteur range ensuite.
 */
export function parcoursDeSequence(s: BrouillonSequence): BrouillonParcours {
  return {
    nom: s.titre,
    mot: s.mot,
    pourLe: s.pourLe,
    blocs: [{ cle: 'b1', nature: 'libre', titre: '', etapes: s.elements }],
  };
}

/** Ce que la banque sait d'une ressource, pour la nommer quand l'ébauche ne donne que son code. */
export interface RessourceConnue {
  readonly titre: string;
  readonly sorte: string;
}

/** La réponse de l'ébauche (P4) : une proposition, rien d'écrit. */
export interface EbaucheParcours {
  readonly nom: string;
  readonly blocs: readonly BlocParcoursBody[];
  /** La raison de chaque étape, par `genre:code`. */
  readonly raisons: Readonly<Record<string, string>>;
}

/**
 * L'ébauche REMPLACE les blocs ; le nom que le tuteur a déjà écrit, son mot et
 * sa date restent. Une ressource que la banque ne nomme pas (un chapitre non
 * ouvert) se dit par son genre — jamais par son code (MV-UX-04).
 */
export function parcoursDEbauche(
  ebauche: EbaucheParcours,
  connues: ReadonlyMap<string, RessourceConnue>,
  avant: BrouillonParcours,
): BrouillonParcours {
  return {
    ...avant,
    nom: avant.nom.trim() === '' ? ebauche.nom : avant.nom,
    blocs: ebauche.blocs.map((bloc, i) => ({
      cle: `b${i + 1}`,
      nature: bloc.nature,
      titre: bloc.titre ?? '',
      etapes: bloc.etapes.map((e) => {
        const cle = cleDElement(e);
        const connue = connues.get(cle);
        return {
          genre: e.genre,
          code: e.code,
          titre: connue?.titre ?? LIBELLES_GENRE_DE_GESTE[e.genre],
          sorte: connue?.sorte ?? LIBELLES_GENRE_DE_GESTE[e.genre],
          raison: e.raison ?? ebauche.raisons[cle] ?? '',
        };
      }),
    })),
  };
}

/** « 3 sur 7 » — LE nombre d'un parcours, chez l'élève, le parent et le tuteur. */
export const faitesSurTotal = (faites: number, total: number): string => `${faites} sur ${total}`;

/** La date civile du jour, dans le fuseau de la console. */
export function aujourdhuiConsole(maintenant: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: FUSEAU_CONSOLE }).format(maintenant);
}

/** En retard : la date butoir est passée et le parcours n'est ni terminé ni retiré. */
export function parcoursEnRetard(
  p: { readonly pourLe: string | null; readonly etat: EtatParcours },
  aujourdhui: string,
): boolean {
  return (
    p.pourLe !== null &&
    p.pourLe.slice(0, 10) < aujourdhui &&
    (p.etat === 'en_cours' || p.etat === 'en_pause')
  );
}

export const LIBELLES_ETAT_PARCOURS: Readonly<Record<EtatParcours, string>> = {
  en_cours: 'En cours',
  en_pause: 'En pause',
  termine: 'Terminé',
  archive: 'Retiré',
};

/** Une étape telle que le suivi la montre : celle d'un parcours ou celle d'une séquence. */
export type EtapeSuivie = ElementSequenceDto & { readonly raison?: string | null };

/**
 * UN parcours dans le suivi du tuteur. Deux sources, une forme : la route des
 * parcours (P1) et, tant qu'elle n'est pas servie, les séquences d'avant —
 * qui s'affichent comme des parcours déposés par le tuteur.
 */
export interface ParcoursSuivi {
  readonly id: string;
  readonly nom: string;
  readonly deLaPartDe: string | null;
  readonly mot: string | null;
  readonly creeLe: string;
  readonly pourLe: string | null;
  readonly etat: EtatParcours;
  readonly pauseLe: string | null;
  readonly faites: number;
  readonly total: number;
  readonly enRetard: boolean;
  /** Ce tuteur l'a déposé et peut le retoucher (liste à plat : `?sequence=`). */
  readonly peutModifier: boolean;
  /** Ce tuteur peut le retirer de la liste de l'élève. */
  readonly peutRetirer: boolean;
  readonly blocs: readonly { readonly titre: string; readonly etapes: readonly EtapeSuivie[] }[];
}

export function suiviDeParcours(p: ParcoursEleveDto, aujourdhui: string): ParcoursSuivi {
  return {
    id: p.id,
    nom: p.nom,
    deLaPartDe: p.deLaPartDe,
    mot: p.mot,
    creeLe: p.creeLe,
    pourLe: p.pourLe,
    etat: p.etat,
    pauseLe: p.pauseLe,
    faites: p.faites,
    total: p.total,
    enRetard: parcoursEnRetard(p, aujourdhui),
    /* La retouche passe encore par la route des séquences, qui remplace une
       liste À PLAT : on ne l'offre pas sur un parcours rangé en plusieurs
       blocs, qu'elle déferait. */
    peutModifier: p.peutModifier && p.blocs.length <= 1,
    peutRetirer: p.peutSupprimer,
    blocs: p.blocs.map((bloc) => ({ titre: bloc.titre, etapes: bloc.etapes })),
  };
}

const ETAT_DE_SEQUENCE: Readonly<Record<SequenceTuteurDto['etat'], EtatParcours>> = {
  en_cours: 'en_cours',
  echue: 'en_cours',
  terminee: 'termine',
  retiree: 'archive',
};

export function suiviDeSequence(s: SequenceTuteurDto, aujourdhui: string): ParcoursSuivi {
  const etat = ETAT_DE_SEQUENCE[s.etat];
  return {
    id: s.id,
    nom: s.titre,
    deLaPartDe: s.deLaPartDe,
    mot: s.mot,
    creeLe: s.deposeeLe,
    pourLe: s.pourLe,
    etat,
    pauseLe: null,
    faites: s.elements.filter((e) => e.fait).length,
    total: s.elements.length,
    enRetard: parcoursEnRetard({ pourLe: s.pourLe, etat }, aujourdhui),
    peutModifier: s.laMienne && etat === 'en_cours',
    peutRetirer: s.laMienne && etat !== 'archive',
    blocs: [{ titre: LIBELLES_NATURE_BLOC.libre, etapes: s.elements }],
  };
}

/* ─────────────────────────── la séance ─────────────────────────── */

/** 15, 30, … 180 — les durées qu'une séance accepte (MV-TUT-05). */
export function dureesDeSeance(): number[] {
  const durees: number[] = [];
  for (let d = DUREE_SEANCE_MIN; d <= DUREE_SEANCE_MAX; d += DUREE_SEANCE_PAS) durees.push(d);
  return durees;
}

/**
 * L'instant ISO d'une date et d'une heure SAISIES DANS LE FUSEAU DE LA CONSOLE
 * (`FUSEAU_CONSOLE`) — celui où `formatDateHeure` les relira. Le navigateur du
 * tuteur peut être ailleurs : on ne lui confie pas le décalage.
 */
export function instantDeSeance(date: string, heure: string): string {
  const naif = Date.parse(`${date}T${heure}:00Z`);
  const lu = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSEAU_CONSOLE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(new Date(naif))
    .reduce<Record<string, string>>((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const vu = Date.parse(`${lu.year}-${lu.month}-${lu.day}T${lu.hour}:${lu.minute}:00Z`);
  return new Date(naif + (naif - vu)).toISOString();
}

/** La date civile et l'heure d'un instant, dans le fuseau de la console : de quoi remplir les deux champs. */
export function dateEtHeureDeSeance(iso: string): { date: string; heure: string } {
  const lu = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSEAU_CONSOLE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(new Date(iso))
    .reduce<Record<string, string>>((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return { date: `${lu.year}-${lu.month}-${lu.day}`, heure: `${lu.hour}:${lu.minute}` };
}

export interface BrouillonSeance {
  readonly date: string;
  readonly heure: string;
  readonly dureeMin: number;
  readonly etat: EtatSeanceTutorat;
  readonly notes: string;
  readonly suites: string;
}

/** L'heure proposée d'une séance : après l'école. */
export const HEURE_DE_SEANCE_PAR_DEFAUT = '16:30';

/** Une séance neuve : dans sept jours, 16 h 30, une heure, à venir. */
export function brouillonDeSeanceNeuve(aujourdhui: string): BrouillonSeance {
  return {
    date: ajouterJours(aujourdhui, 7),
    heure: HEURE_DE_SEANCE_PAR_DEFAUT,
    dureeMin: DUREE_SEANCE_PAR_DEFAUT,
    etat: 'a_venir',
    notes: '',
    suites: '',
  };
}

/**
 * Le brouillon d'une séance existante. Une séance « à venir » dont la date est
 * passée s'ouvre sur « Séance faite » : c'est ce que le tuteur vient consigner.
 */
export function brouillonDeSeance(seance: SeanceTutoratDto, maintenant: Date): BrouillonSeance {
  const { date, heure } = dateEtHeureDeSeance(seance.debut);
  const passee = Date.parse(seance.debut) <= maintenant.getTime();
  return {
    date,
    heure,
    dureeMin: seance.dureeMin,
    etat: seance.etat === 'a_venir' && passee ? 'faite' : seance.etat,
    notes: seance.notes ?? '',
    suites: seance.suites ?? '',
  };
}

/** Ce qui manque à une séance pour partir. Vide : elle peut. */
export function reglesDeSeance(b: BrouillonSeance): string[] {
  const manques: string[] = [];
  if (!estDateCivile(b.date)) manques.push('Indiquez la date de la séance.');
  if (!/^\d{2}:\d{2}$/.test(b.heure)) manques.push('Indiquez l’heure de la séance.');
  return manques;
}

/**
 * Le corps d'une séance, pour `POST …/seances` comme pour `PATCH /seances/:id`.
 * Pour une annulation, « Convenu pour la prochaine fois » n'est PAS envoyé
 * rempli : le champ est masqué, il ne doit pas s'enregistrer (écart corrigé
 * avec la maquette, MV-TUT-05).
 */
export function corpsDeSeance(b: BrouillonSeance): {
  debut: string;
  dureeMin: number;
  etat: EtatSeanceTutorat;
  notes: string | null;
  suites: string | null;
} {
  return {
    debut: instantDeSeance(b.date, b.heure),
    dureeMin: b.dureeMin,
    etat: b.etat,
    notes: b.notes.trim() === '' ? null : b.notes.trim(),
    suites: b.etat === 'annulee' || b.suites.trim() === '' ? null : b.suites.trim(),
  };
}

/* ─────────────────────────── les adresses ─────────────────────────── */

/**
 * Les onglets de la fiche : une adresse chacun, rechargeable (MV-TUT-02).
 * « Travaux et corrections » et « Évolution » (MV-TUT-07, 08) sont de la
 * seconde tranche.
 */
export const ONGLETS_FICHE = ['portrait', 'sequences', 'seances', 'travaux', 'evolution'] as const;
export type OngletFiche = (typeof ONGLETS_FICHE)[number];

export function ongletDeLAdresse(brut: string | undefined): OngletFiche {
  return ONGLETS_FICHE.find((o) => o === brut) ?? 'portrait';
}

export const adressesManuelEleve = {
  fiche: (eleveId: string, onglet: OngletFiche = 'portrait'): string =>
    onglet === 'portrait'
      ? `/eleves/${encodeURIComponent(eleveId)}/manuel`
      : `/eleves/${encodeURIComponent(eleveId)}/manuel/${onglet}`,
  composer: (eleveId: string, sequenceId?: string): string =>
    `/eleves/${encodeURIComponent(eleveId)}/manuel/composer${
      sequenceId === undefined ? '' : `?sequence=${encodeURIComponent(sequenceId)}`
    }`,
  /** La préparation de séance (MV-TUT-04) : une sous-page, pas un onglet. */
  preparation: (eleveId: string): string =>
    `/eleves/${encodeURIComponent(eleveId)}/manuel/preparation`,
  /** Le rapport unique (MV-TUT-11) : une lecture longue, donc une sous-page. */
  rapport: (eleveId: string): string => `/eleves/${encodeURIComponent(eleveId)}/manuel/rapport`,
  /** « Travaux et corrections », filtré sur une erreur nommée (« Voir les copies »). */
  travauxDeLErreur: (eleveId: string, codeErreur: string): string =>
    `/eleves/${encodeURIComponent(eleveId)}/manuel/travaux?erreur=${encodeURIComponent(codeErreur)}`,
} as const;

/**
 * Les adresses du parcours (P3) : le compositeur, ouvert sur un élève ou sur
 * une classe, et le suivi d'une classe. Le suivi d'un élève vit dans sa fiche,
 * à l'onglet « sequences ».
 */
export const adressesParcours = {
  composer: (destination: { readonly eleve: string } | { readonly classe: string }): string =>
    'eleve' in destination
      ? `/parcours/nouveau?eleve=${encodeURIComponent(destination.eleve)}`
      : `/parcours/nouveau?classe=${encodeURIComponent(destination.classe)}`,
  suiviClasse: (classeId: string): string => `/classes/${encodeURIComponent(classeId)}/parcours`,
} as const;
